import { Controller } from "@hotwired/stimulus"
import { MAP_STYLE, MAP_CONFIG } from "../custom/map_style"

export default class extends Controller {
  static targets = ["map"]
  static values = {
    sourcePostcode: String,
    mappedPostcode: String,
    mappedLat: Number,
    mappedLng: Number,
    token: String
  }

  async connect() {
    if (!this.tokenValue || !this.hasMapTarget) return

    const mapboxgl = await this.loadMapboxGL()
    if (!mapboxgl) return

    // If we have mapped coordinates, use those as center
    const centerLat = this.mappedLatValue || 51.505
    const centerLng = this.mappedLngValue || -0.09

    this.map = new mapboxgl.Map({
      container: this.mapTarget,
      style: MAP_STYLE,
      config: MAP_CONFIG,
      center: [centerLng, centerLat],
      zoom: 13,
      accessToken: this.tokenValue
    })

    this.map.addControl(new mapboxgl.NavigationControl(), "top-right")

    // Add mapped address pin (orange) if we have coordinates
    if (this.mappedLatValue && this.mappedLngValue) {
      new mapboxgl.Marker({ color: "#f97316" })
        .setLngLat([this.mappedLngValue, this.mappedLatValue])
        .setPopup(new mapboxgl.Popup().setHTML(`<strong>Mapped</strong><br>${this.mappedPostcodeValue || "Unknown"}`))
        .addTo(this.map)
    }

    // Geocode the source postcode and add blue pin
    if (this.sourcePostcodeValue) {
      this.geocodeAndPin(mapboxgl, this.sourcePostcodeValue, "#3b82f6", "Source")
    }
  }

  async geocodeAndPin(mapboxgl, postcode, color, label) {
    try {
      const url = `https://api.mapbox.com/search/geocode/v6/forward?q=${encodeURIComponent(postcode)}&country=gb&access_token=${this.tokenValue}`
      const response = await fetch(url)
      const data = await response.json()

      if (data.features && data.features.length > 0) {
        const [lng, lat] = data.features[0].geometry.coordinates

        new mapboxgl.Marker({ color })
          .setLngLat([lng, lat])
          .setPopup(new mapboxgl.Popup().setHTML(`<strong>${label}</strong><br>${postcode}`))
          .addTo(this.map)

        // Fit bounds to show both markers if we have mapped coordinates too
        if (this.mappedLatValue && this.mappedLngValue) {
          const bounds = new mapboxgl.LngLatBounds()
          bounds.extend([lng, lat])
          bounds.extend([this.mappedLngValue, this.mappedLatValue])
          this.map.fitBounds(bounds, { padding: 60, maxZoom: 15 })
        } else {
          this.map.flyTo({ center: [lng, lat], zoom: 14 })
        }
      }
    } catch (error) {
      console.error("Geocode failed:", error)
    }
  }

  async loadMapboxGL() {
    if (window.mapboxgl) return window.mapboxgl

    try {
      const module = await import("mapbox-gl")
      window.mapboxgl = module.default
      return window.mapboxgl
    } catch {
      console.error("Failed to load mapbox-gl")
      return null
    }
  }

  disconnect() {
    this.map?.remove()
  }
}
